/**
 * LinkOrderDialog — the order-linking flow for an order-less scan
 * (ORDER_ADOPTION_HANDOFF.md §5.B).
 *
 * The spec called this chip "Link Order" specifically to avoid the word
 * "Confirm". That was overruled: technicians already read "Confirm" as "this row
 * wants me", and a new verb costs more in retraining than the ambiguity costs in
 * confusion. The two Confirm chips are kept apart by colour (blue here, amber
 * for a field mismatch), by tooltip, and by the fact that they can never both
 * appear on one row — a scan-made order has no HIS order to disagree with. The
 * file and route keep the Link Order name; only the label changed.
 *
 * A study whose accession matched nothing at ingestion time gets a throwaway
 * order invented from its DICOM headers, while the real HIS order sits PENDING
 * with no study. This dialog is how a technician tells the system which HIS
 * order the scan actually belongs to.
 *
 * The input is a free-text accession box, which is only acceptable because the
 * commit is gated on a SIDE-BY-SIDE confirmation of both patients. A mistyped
 * accession does not usually resolve to nothing — it resolves to somebody
 * else's plausible-looking order — so showing only the HIS side would turn this
 * into a rubber stamp. Both sides, with per-field match marks, plus the
 * modality and both timestamps, is what makes a wrong match catchable.
 *
 * Three steps, and nothing is written before step 3:
 *   1. type the accession            → POST /lookup-order-by-accession
 *   2. compare both identities       → (no request)
 *   3. commit                        → POST /link-order-to-scan
 */

import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Spin, Tooltip, message } from 'antd';
import { makePostCall } from '../utils/helper';

const moment = require('moment');

const DATE_TIME = 'DD.MM.YYYY HH:mm';

type Identity = {
  pacs_ord_id: number | null;
  acc_no: string | null;
  his_ord_no: string | null;
  diag_no: string | null;
  diag_desc: string | null;
  modality: string | null;
  his_status: string | null;
  center_code: string | null;
  center_name: string | null;
  req_time: string | null;
  pat_pin: string | null;
  pat_name: string | null;
  pat_dob: string | null;
  pat_sex: string | null;
  pat_age: string | null;
  workflow_status: string | null;
};

type ScanSide = Identity & {
  study_uid: string | null;
  scan_received_at: string | null;
  study_dt_tm: string | null;
  archive_patient_id: string | null;
  archive_accession: string | null;
  archive_modalities: string[];
};

type Candidate = {
  order: Identity;
  has_study: boolean;
  linkable: boolean;
  reject_code: string | null;
  reject_message: string | null;
};

const STYLE_ID = 'link-order-dialog-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
  .lo-cmp { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; }
  .lo-cmp thead th {
    padding: 7px 10px; text-align: left; font-weight: 600; font-size: 10.5px;
    letter-spacing: .04em; text-transform: uppercase; color: #64748B;
    background: #F8FAFC; border-bottom: 1px solid #E2E8F0; white-space: nowrap;
  }
  .lo-cmp tbody td { padding: 7px 10px; border-bottom: 1px solid #F1F5F9; vertical-align: top; }
  .lo-cmp tbody tr:last-child td { border-bottom: none; }
  .lo-field { font-weight: 600; color: #334155; white-space: nowrap; }
  .lo-val { color: #475569; word-break: break-word; }
  .lo-arrow { color: #CBD5E1; text-align: center; width: 22px; }
  .lo-sub { display: block; font-size: 10px; color: #94A3B8; margin-top: 2px; }
  .lo-mark { width: 20px; text-align: center; }
  .lo-cand {
    display: block; padding: 8px 10px; border: 1px solid #E2E8F0; border-radius: 6px;
    margin-bottom: 8px; background: #fff;
  }
  .lo-cand--bad { background: #FEF2F2; border-color: #FECACA; }
  `;
  document.head.appendChild(el);
}

const dash = <span style={{ color: '#CBD5E1' }}>—</span>;
const fmt = (v?: string | null) => (v && String(v).trim() ? String(v) : null);
const fmtTime = (v?: string | null) => (v && moment(v).isValid() ? moment(v).format(DATE_TIME) : null);

/** DICOM DOBs arrive as YYYYMMDD from some paths and ISO from others. */
const fmtDob = (v?: string | null) => {
  const raw = fmt(v);
  if (!raw || raw === '*') return null;
  const m = /^\d{8}$/.test(raw) ? moment(raw, 'YYYYMMDD') : moment(raw);
  return m.isValid() ? m.format('DD.MM.YYYY') : raw;
};

const Mark = ({ state }: { state: 'match' | 'differ' | 'unknown' }) => {
  if (state === 'match') {
    return (
      <Tooltip title="Matches">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M13 4.5 6.5 11 3 7.5" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Tooltip>
    );
  }
  if (state === 'differ') {
    return (
      <Tooltip title="Differs — check this is the right patient">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M8 2.2 15 14H1L8 2.2Z" stroke="#F59E0B" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M8 6.4v3.1" stroke="#F59E0B" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="8" cy="11.6" r="0.95" fill="#F59E0B" />
        </svg>
      </Tooltip>
    );
  }
  return <span style={{ color: '#CBD5E1', fontSize: 11 }}>·</span>;
};

/** Loose comparison: case- and whitespace-insensitive, blanks are "unknown". */
const compare = (a?: string | null, b?: string | null): 'match' | 'differ' | 'unknown' => {
  const x = (a ?? '').toString().trim().toUpperCase();
  const y = (b ?? '').toString().trim().toUpperCase();
  if (!x || !y || x === '*' || y === '*') return 'unknown';
  return x === y ? 'match' : 'differ';
};

const Row = ({
  label, scan, his, scanSub, hisSub, mark,
}: {
  label: string;
  scan: React.ReactNode;
  his: React.ReactNode;
  scanSub?: string | null;
  hisSub?: string | null;
  mark?: 'match' | 'differ' | 'unknown';
}) => (
  <tr>
    <td className="lo-field">{label}</td>
    <td className="lo-val">
      {scan}
      {scanSub ? <span className="lo-sub">{scanSub}</span> : null}
    </td>
    <td className="lo-arrow">→</td>
    <td className="lo-val">
      {his}
      {hisSub ? <span className="lo-sub">{hisSub}</span> : null}
    </td>
    <td className="lo-mark">{mark ? <Mark state={mark} /> : null}</td>
  </tr>
);

export const LinkOrderDialog = ({
  open,
  onClose,
  pacsOrdId,
  onLinked,
}: {
  open: boolean;
  onClose: () => void;
  pacsOrdId: number;
  /** Called with the fresh order row after a successful link. */
  onLinked: (order: any) => void;
}) => {
  const [accession, setAccession] = useState('');
  const [looking, setLooking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [scan, setScan] = useState<ScanSide | null>(null);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<number | null>(null);

  // A dialog reopened on another row must not show the previous row's answer.
  useEffect(() => {
    if (!open) return;
    setAccession('');
    setScan(null);
    setCandidates(null);
    setLookupMessage(null);
    setPickedId(null);
    setLooking(false);
    setLinking(false);
  }, [open, pacsOrdId]);

  const handleLookup = () => {
    const acc = accession.trim();
    if (!acc) {
      message.info('Enter the accession number from the HIS order.');
      return;
    }
    setLooking(true);
    setCandidates(null);
    setLookupMessage(null);
    setPickedId(null);
    makePostCall('/lookup-order-by-accession', { accession: acc, pacs_ord_id: pacsOrdId })
      .then(res => {
        const data = res?.data?.data;
        if (!res?.data?.success || !data) {
          setLookupMessage(res?.data?.message || 'Lookup failed.');
          return;
        }
        setScan(data.scan || null);
        setCandidates(data.candidates || []);
        setLookupMessage(data.message || null);
        const linkable = (data.candidates || []).filter((c: Candidate) => c.linkable);
        // One match is the overwhelmingly common case — preselect it so the
        // user's next action is the comparison, not a redundant click.
        if (linkable.length === 1) setPickedId(linkable[0].order.pacs_ord_id);
      })
      .catch(err => {
        console.error('lookup-order-by-accession failed', err);
        setLookupMessage(err?.response?.data?.message || 'Lookup failed.');
      })
      .finally(() => setLooking(false));
  };

  const linkable = (candidates || []).filter(c => c.linkable);
  const rejected = (candidates || []).filter(c => !c.linkable);
  const picked = linkable.find(c => c.order.pacs_ord_id === pickedId) || null;

  const handleLink = () => {
    if (!picked) return;
    setLinking(true);
    makePostCall('/link-order-to-scan', {
      pacs_ord_id: pacsOrdId,
      his_pacs_ord_id: picked.order.pacs_ord_id,
    })
      .then(res => {
        if (!res?.data?.success) {
          message.error(res?.data?.message || 'Linking failed.');
          return;
        }
        message.success(res.data.message || 'Order linked.');
        onLinked(res.data.data);
        onClose();
      })
      .catch(err => {
        console.error('link-order-to-scan failed', err);
        message.error(err?.response?.data?.message || 'Linking failed.');
      })
      .finally(() => setLinking(false));
  };

  const his = picked?.order || null;

  return (
    <Modal
      title="Link this scan to its HIS order"
      open={open}
      onCancel={e => {
        (e as any)?.stopPropagation?.();
        onClose();
      }}
      width={720}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button
          key="link"
          type="primary"
          disabled={!picked}
          loading={linking}
          onClick={handleLink}
        >
          {picked ? `Confirm order ${picked.order.his_ord_no || `#${picked.order.pacs_ord_id}`}` : 'Confirm order'}
        </Button>,
      ]}
    >
      <p style={{ marginBottom: 12, color: '#64748b' }}>
        This scan arrived without a matching HIS order, so the system created a placeholder from the
        DICOM headers. Enter the accession number from the real HIS order, then check that both sides
        describe the same patient before linking.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <Input
          value={accession}
          onChange={e => setAccession(e.target.value)}
          onPressEnter={handleLookup}
          placeholder="Accession number"
          style={{ maxWidth: 260 }}
          autoFocus
        />
        <Button type="default" loading={looking} onClick={handleLookup}>Find order</Button>
      </div>

      {looking && (
        <div style={{ padding: '16px 0', textAlign: 'center' }}><Spin size="small" /></div>
      )}

      {!looking && lookupMessage && (
        <Alert type="warning" showIcon message={lookupMessage} style={{ marginBottom: 12 }} />
      )}

      {!looking && rejected.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {rejected.map(c => (
            <Alert
              key={c.order.pacs_ord_id}
              type="error"
              showIcon
              message={c.reject_message}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      )}

      {/* More than one order can hold the same accession: the partial unique
          index that should prevent it is missing on some deployments, and
          SEC/MLK/HTC share one box. Make the user pick rather than guessing. */}
      {!looking && linkable.length > 1 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: '#334155', fontSize: 12 }}>
            {linkable.length} orders carry this accession — choose the right one
          </div>
          <Radio.Group
            value={pickedId}
            onChange={e => setPickedId(e.target.value)}
            style={{ width: '100%' }}
          >
            {linkable.map(c => (
              <Radio key={c.order.pacs_ord_id} value={c.order.pacs_ord_id} className="lo-cand">
                <strong>{c.order.pat_name || '(no name)'}</strong>{' '}
                <span style={{ color: '#64748B' }}>
                  YH {c.order.pat_pin || '—'} · order {c.order.his_ord_no || `#${c.order.pacs_ord_id}`} ·{' '}
                  {c.order.diag_desc || '(no description)'} ·{' '}
                  {fmtTime(c.order.req_time) || 'no request time'}
                </span>
              </Radio>
            ))}
          </Radio.Group>
        </div>
      )}

      {!looking && picked && scan && (
        <>
          <div style={{ fontWeight: 600, marginBottom: 6, color: '#334155', fontSize: 12 }}>
            Confirm this is the same patient
          </div>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
            <table className="lo-cmp">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>From scan</th>
                  <th />
                  <th>From HIS order</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <Row
                  label="YH No."
                  scan={fmt(scan.pat_pin) || dash}
                  his={fmt(his!.pat_pin) || dash}
                  // What the images themselves carry, when it differs from the
                  // placeholder order's value — this is the field the archive
                  // write below will change.
                  scanSub={
                    scan.archive_patient_id && scan.archive_patient_id !== scan.pat_pin
                      ? `images: ${scan.archive_patient_id}`
                      : null
                  }
                  mark={compare(scan.pat_pin, his!.pat_pin)}
                />
                <Row
                  label="Name"
                  scan={fmt(scan.pat_name) || dash}
                  his={fmt(his!.pat_name) || dash}
                  mark={compare(scan.pat_name, his!.pat_name)}
                />
                <Row
                  label="DOB / Sex"
                  scan={`${fmtDob(scan.pat_dob) || '—'} / ${fmt(scan.pat_sex) === '*' ? '—' : (fmt(scan.pat_sex) || '—')}`}
                  his={`${fmtDob(his!.pat_dob) || '—'} / ${fmt(his!.pat_sex) || '—'}`}
                  mark={compare(scan.pat_dob, his!.pat_dob)}
                />
                <Row
                  label="Modality"
                  scan={(scan.archive_modalities || []).join(', ') || fmt(scan.modality) || dash}
                  his={fmt(his!.modality) || dash}
                  scanSub={scan.archive_modalities?.length ? 'received' : null}
                  hisSub={his!.modality ? 'expected' : 'no item mapping yet'}
                  mark={
                    scan.archive_modalities?.length && his!.modality
                      ? (his!.modality.split(',').some(m =>
                          scan.archive_modalities.includes(m.trim().toUpperCase()))
                          ? 'match' : 'differ')
                      : 'unknown'
                  }
                />
                <Row
                  label="Study"
                  scan={fmt(scan.diag_desc) === '*' ? dash : (fmt(scan.diag_desc) || dash)}
                  his={fmt(his!.diag_desc) || dash}
                />
                <Row
                  label="Time"
                  scan={fmtTime(scan.scan_received_at) || fmtTime(scan.study_dt_tm) || dash}
                  his={fmtTime(his!.req_time) || dash}
                  scanSub="scanned"
                  hisSub="ordered"
                />
                <Row
                  label="Centre"
                  scan={fmt(scan.center_name) || fmt(scan.center_code) || dash}
                  his={fmt(his!.center_name) || fmt(his!.center_code) || dash}
                  mark={compare(scan.center_code, his!.center_code)}
                />
              </tbody>
            </table>
          </div>

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="What happens when you confirm"
            description={
              <span style={{ fontSize: 12 }}>
                The HIS order's details move onto this scan's order, and HIS order
                #{his!.pacs_ord_id} is deactivated. The images are relabelled in the archive with
                the HIS patient ID and accession — that is what makes prior studies load in the
                viewer. Any draft report, assignment, notes and uploads already on this scan are
                kept. If the archive cannot be reached, nothing at all is changed.
              </span>
            }
          />
        </>
      )}
    </Modal>
  );
};

export default LinkOrderDialog;
